import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { getMatch, recordBall, undoBall, setBowler, startInnings, endInnings, editBall } from "@/lib/api";
import { toast } from "sonner";
import { Undo2, Flag, Eye, ChevronRight, Share2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { OfflineQueuedError } from "@/lib/api";
import { recordBallLocal, undoBallLocal, setBowlerLocal, editBallLocal } from "@/lib/localScorer";

export default function LiveScoring() {
  const { id } = useParams();
  const nav = useNavigate();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Dialogs
  const [setupOpen, setSetupOpen] = useState(false);
  const [strikerId, setStrikerId] = useState("");
  const [nonStrikerId, setNonStrikerId] = useState("");
  const [bowlerSelectId, setBowlerSelectId] = useState("");

  const [bowlerDialogOpen, setBowlerDialogOpen] = useState(false);
  const [newBowlerId, setNewBowlerId] = useState("");

  const [wicketOpen, setWicketOpen] = useState(false);
  const [wicketType, setWicketType] = useState("bowled");
  const [wicketRuns, setWicketRuns] = useState(0);
  const [wicketExtras, setWicketExtras] = useState("none");
  const [outPlayerId, setOutPlayerId] = useState("");
  const [newBatsmanId, setNewBatsmanId] = useState("");

  const [extraOpen, setExtraOpen] = useState(false);
  const [extraType, setExtraType] = useState("wide");
  const [extraRuns, setExtraRuns] = useState(0);

  const [endInningsOpen, setEndInningsOpen] = useState(false);

  // Edit ball
  const [editOpen, setEditOpen] = useState(false);
  const [editBallIdx, setEditBallIdx] = useState(null);
  const [editRuns, setEditRuns] = useState(0);
  const [editExtras, setEditExtras] = useState("none");
  const [editIsWicket, setEditIsWicket] = useState(false);
  const [editWicketType, setEditWicketType] = useState("bowled");

  const refresh = useCallback(async () => {
    try {
      const m = await getMatch(id);
      setMatch(m);
    } catch (e) {
      toast.error("Match not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh after offline queue drains
  useEffect(() => {
    const handler = () => refresh();
    window.addEventListener("crickpad-queue-drained", handler);
    return () => window.removeEventListener("crickpad-queue-drained", handler);
  }, [refresh]);

  const shareSpectatorLink = async () => {
    const url = `${window.location.origin}/watch/${id}`;
    const shareData = {
      title: "CrickPad — Live Match",
      text: "Follow this match live on CrickPad",
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Spectator link copied");
      }
    } catch (e) {
      // user cancelled native share — silently ignore
      if (e?.name !== "AbortError") {
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Spectator link copied");
        } catch {
          toast.error("Couldn't copy link");
        }
      }
    }
  };

  // Determine if we need to setup current innings
  const currentInning = match?.innings?.[match.current_innings];
  const battingTeam = match?.teams?.[currentInning?.batting_team_idx];
  const bowlingTeam = match?.teams?.[currentInning?.bowling_team_idx];

  useEffect(() => {
    if (!match) return;
    if (match.status === "completed") return;
    if (currentInning && !currentInning.started) {
      // Open setup dialog and pre-fill defaults
      const bp = battingTeam.players;
      const blp = bowlingTeam.players;
      setStrikerId(bp[0]?.id || "");
      setNonStrikerId(bp[1]?.id || "");
      setBowlerSelectId(blp[0]?.id || "");
      setSetupOpen(true);
    } else if (currentInning && currentInning.started && !currentInning.bowler_id && match.status === "live") {
      // need new bowler at start of next over
      setNewBowlerId("");
      setBowlerDialogOpen(true);
    }
  }, [match]); // eslint-disable-line

  const submitSetup = async () => {
    if (!strikerId || !nonStrikerId || !bowlerSelectId) {
      toast.error("Select striker, non-striker and bowler");
      return;
    }
    if (strikerId === nonStrikerId) {
      toast.error("Striker and non-striker must be different");
      return;
    }
    setBusy(true);
    try {
      const m = await startInnings(id, {
        striker_id: strikerId,
        non_striker_id: nonStrikerId,
        bowler_id: bowlerSelectId,
      });
      setMatch(m);
      setSetupOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const submitNewBowler = async () => {
    if (!newBowlerId) return toast.error("Select a bowler");
    setBusy(true);
    try {
      const m = await setBowler(id, newBowlerId, () => setBowlerLocal(match, newBowlerId));
      setMatch(m);
      setBowlerDialogOpen(false);
    } catch (e) {
      if (e instanceof OfflineQueuedError) {
        if (e.optimisticMatch) setMatch(e.optimisticMatch);
        setBowlerDialogOpen(false);
        toast.success("Offline — bowler change queued.");
      } else {
        toast.error(e.response?.data?.detail || "Failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const ball = async (data) => {
    if (busy || !match || match.status !== "live") return;
    setBusy(true);
    try {
      const m = await recordBall(id, data, () => recordBallLocal(match, data));
      setMatch(m);
    } catch (e) {
      if (e instanceof OfflineQueuedError) {
        if (e.optimisticMatch) setMatch(e.optimisticMatch);
        toast.success("Offline — ball queued. Will sync when online.");
      } else {
        toast.error(e.response?.data?.detail || "Failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const onUndo = async () => {
    setBusy(true);
    try {
      const m = await undoBall(id, () => undoBallLocal(match));
      setMatch(m);
      toast.success("Last ball undone");
    } catch (e) {
      if (e instanceof OfflineQueuedError) {
        if (e.optimisticMatch) setMatch(e.optimisticMatch);
        toast.success("Offline — undo queued.");
      } else {
        toast.error(e.response?.data?.detail || "Nothing to undo");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitWicket = async () => {
    setBusy(true);
    const data = {
      runs: Number(wicketRuns) || 0,
      extras_type: wicketExtras,
      is_wicket: true,
      wicket_type: wicketType,
      out_player_id: outPlayerId || null,
      new_batsman_id: newBatsmanId || null,
    };
    const closeAndReset = () => {
      setWicketOpen(false);
      setWicketType("bowled");
      setWicketRuns(0);
      setWicketExtras("none");
      setOutPlayerId("");
      setNewBatsmanId("");
    };
    try {
      const m = await recordBall(id, data, () => recordBallLocal(match, data));
      setMatch(m);
      closeAndReset();
    } catch (e) {
      if (e instanceof OfflineQueuedError) {
        if (e.optimisticMatch) setMatch(e.optimisticMatch);
        closeAndReset();
        toast.success("Offline — wicket queued.");
      } else {
        toast.error(e.response?.data?.detail || "Failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const submitExtra = async () => {
    setBusy(true);
    const data = {
      runs: Number(extraRuns) || 0,
      extras_type: extraType,
      is_wicket: false,
    };
    const closeAndReset = () => {
      setExtraOpen(false);
      setExtraRuns(0);
      setExtraType("wide");
    };
    try {
      const m = await recordBall(id, data, () => recordBallLocal(match, data));
      setMatch(m);
      closeAndReset();
    } catch (e) {
      if (e instanceof OfflineQueuedError) {
        if (e.optimisticMatch) setMatch(e.optimisticMatch);
        closeAndReset();
        toast.success("Offline — extra queued.");
      } else {
        toast.error(e.response?.data?.detail || "Failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const onEndInnings = async () => {
    setBusy(true);
    try {
      const m = await endInnings(id);
      setMatch(m);
      setEndInningsOpen(false);
      toast.success("Innings ended");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const openEditBall = (ball, idx) => {
    setEditBallIdx(idx);
    let r = ball.runs || 0;
    if (ball.extras_type === "wide") r = (ball.extras_runs || 1) - 1;
    else if (ball.extras_type === "bye" || ball.extras_type === "leg_bye") r = ball.extras_runs || 0;
    setEditRuns(r);
    setEditExtras(ball.extras_type || "none");
    setEditIsWicket(!!ball.is_wicket);
    setEditWicketType(ball.wicket_type || "bowled");
    setEditOpen(true);
  };

  const submitEditBall = async () => {
    if (editBallIdx === null) return;
    setBusy(true);
    const data = {
      runs: Number(editRuns) || 0,
      extras_type: editExtras,
      is_wicket: editIsWicket,
      wicket_type: editIsWicket ? editWicketType : null,
    };
    try {
      const m = await editBall(
        id,
        match.current_innings,
        editBallIdx,
        data,
        () => editBallLocal(match, match.current_innings, editBallIdx, data),
      );
      setMatch(m);
      setEditOpen(false);
      toast.success("Ball updated");
    } catch (e) {
      if (e instanceof OfflineQueuedError) {
        if (e.optimisticMatch) setMatch(e.optimisticMatch);
        setEditOpen(false);
        toast.success("Offline — edit queued.");
      } else {
        toast.error(e.response?.data?.detail || "Failed");
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-muted-foreground uppercase text-xs tracking-[0.3em]">Loading...</div>;
  if (!match) return <div className="p-12 text-center">Match not found</div>;

  // Match completed view
  if (match.status === "completed") {
    return (
      <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-primary font-bold">Match Complete</p>
        <h1 className="font-display font-black uppercase text-5xl mt-3">{match.result}</h1>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to={`/match/${match.id}`} className="tactical-btn bg-primary text-primary-foreground px-6 py-3 text-sm inline-flex items-center gap-2">
            View Scorecard <Eye className="w-4 h-4" strokeWidth={3} />
          </Link>
          <button onClick={() => nav("/")} className="tactical-btn border border-border px-6 py-3 text-sm">Home</button>
        </div>
      </div>
    );
  }

  const summary = currentInning?.summary;
  const fmt = match.format;
  const lastInningsIdx = fmt === "Test" ? 3 : 1;
  let target = null;
  if (match.current_innings === lastInningsIdx) {
    const battingIdx = currentInning.batting_team_idx;
    let opposingTotal = 0;
    let ownPrior = 0;
    for (let i = 0; i < match.current_innings; i++) {
      const r = match.innings[i].summary.runs;
      if (match.innings[i].batting_team_idx === battingIdx) ownPrior += r;
      else opposingTotal += r;
    }
    target = opposingTotal - ownPrior + 1;
  }
  const ballsRemaining = fmt !== "Test" ? Math.max(0, match.overs * 6 - (summary?.legal_balls || 0)) : null;
  const required = target ? target - (summary?.runs || 0) : null;
  const reqRR = target && ballsRemaining ? ((required / ballsRemaining) * 6).toFixed(2) : null;
  const currentRR = summary && summary.legal_balls > 0 ? ((summary.runs / summary.legal_balls) * 6).toFixed(2) : "0.00";

  // active batsmen / bowler
  const striker = battingTeam?.players.find((p) => p.id === currentInning?.striker_id);
  const nonStriker = battingTeam?.players.find((p) => p.id === currentInning?.non_striker_id);
  const bowler = bowlingTeam?.players.find((p) => p.id === currentInning?.bowler_id);

  const strikerStats = striker ? currentInning.batsmen_stats.find((b) => b.player_id === striker.id) : null;
  const nonStrikerStats = nonStriker ? currentInning.batsmen_stats.find((b) => b.player_id === nonStriker.id) : null;
  const bowlerStats = bowler ? currentInning.bowler_stats.find((b) => b.player_id === bowler.id) : null;

  // Current over balls (with absolute indices for editing)
  const currentOverBalls = currentInning.balls
    .map((b, idx) => ({ ball: b, idx }))
    .filter(({ ball }) => ball.over_num === summary.overs_completed);

  // Available batsmen (not out and not on field)
  const availableBatsmen = battingTeam.players.filter(
    (p) =>
      !currentInning.out_player_ids.includes(p.id) &&
      p.id !== currentInning.striker_id &&
      p.id !== currentInning.non_striker_id
  );

  return (
    <div className="pb-40" data-testid="live-scoring-page">
      {/* STICKY SCOREBOARD */}
      <div className="sticky top-16 z-30 bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="pulse-dot"></span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-destructive font-bold">Live</span>
              <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
                {match.format} · Inn {match.current_innings + 1}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={shareSpectatorLink}
                data-testid="share-spectator-btn"
                className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                title="Share live spectator link"
              >
                <Share2 className="w-3 h-3" /> Share
              </button>
              <Link
                to={`/match/${match.id}`}
                data-testid="view-scorecard-btn"
                className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-primary inline-flex items-center gap-1 ml-3"
              >
                <Eye className="w-3 h-3" /> Scorecard
              </Link>
              <button
                onClick={() => setEndInningsOpen(true)}
                data-testid="end-innings-btn"
                className="text-xs uppercase tracking-[0.2em] text-destructive hover:text-destructive/80 inline-flex items-center gap-1 ml-3"
              >
                <Flag className="w-3 h-3" /> End Innings
              </button>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-12 gap-4 items-end">
            <div className="col-span-12 sm:col-span-6">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{battingTeam?.name}</div>
              <div className="flex items-baseline gap-3">
                <span className="font-display font-black text-6xl sm:text-7xl leading-none stat-tabular">
                  {summary?.runs || 0}
                </span>
                <span className="font-display font-black text-3xl text-muted-foreground stat-tabular">/{summary?.wickets || 0}</span>
                <span className="font-mono text-base text-muted-foreground stat-tabular">({summary?.overs})</span>
              </div>
            </div>
            <div className="col-span-12 sm:col-span-6 grid grid-cols-3 gap-3 text-right">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">CRR</div>
                <div className="font-mono text-lg text-foreground stat-tabular">{currentRR}</div>
              </div>
              {target && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Target</div>
                  <div className="font-mono text-lg text-primary stat-tabular">{target}</div>
                </div>
              )}
              {reqRR && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">RRR</div>
                  <div className="font-mono text-lg text-destructive stat-tabular">{reqRR}</div>
                </div>
              )}
            </div>
          </div>

          {target && (
            <p className="mt-2 text-xs text-muted-foreground">
              Need <span className="text-primary font-bold stat-tabular">{required}</span> runs from{" "}
              <span className="text-primary font-bold stat-tabular">{ballsRemaining}</span> balls
            </p>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 grid lg:grid-cols-12 gap-6">
        {/* LEFT: Active players + this over */}
        <div className="lg:col-span-7 space-y-4">
          <div className="border border-border bg-card p-5">
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold mb-3">Batting</div>
            <div className="space-y-2">
              <BatsmanRow stats={strikerStats} name={striker?.name} striker />
              <BatsmanRow stats={nonStrikerStats} name={nonStriker?.name} />
            </div>
          </div>

          <div className="border border-border bg-card p-5">
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold mb-3">Bowling</div>
            <BowlerRow stats={bowlerStats} name={bowler?.name} />
          </div>

          {/* This over */}
          <div className="border border-border bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">
                This Over · {summary.overs_completed + 1}
              </div>
              <button
                onClick={onUndo}
                data-testid="undo-btn"
                disabled={busy}
                className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-destructive inline-flex items-center gap-1.5 disabled:opacity-40"
              >
                <Undo2 className="w-3.5 h-3.5" /> Undo
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {currentOverBalls.length === 0 && (
                <span className="text-sm text-muted-foreground italic">First ball of the over</span>
              )}
              {currentOverBalls.map(({ ball, idx }) => (
                <BallChip key={idx} ball={ball} onClick={() => openEditBall(ball, idx)} />
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT: Match info */}
        <aside className="lg:col-span-5 space-y-4">
          <div className="border border-border bg-card p-5">
            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold mb-3">Match Info</div>
            <dl className="space-y-2 text-sm font-mono">
              <Row k="Format" v={match.format} />
              <Row k="Overs" v={match.overs} />
              <Row k="Toss" v={`${match.teams[match.toss_winner === "A" ? 0 : 1].name} (${match.toss_decision})`} />
              <Row k="Innings" v={`${match.current_innings + 1} of ${match.format === "Test" ? 4 : 2}`} />
              {match.venue && <Row k="Venue" v={match.venue} />}
            </dl>
          </div>
        </aside>
      </div>

      {/* FIXED KEYPAD */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-background border-t border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-4">
          <div className="grid grid-cols-6 sm:grid-cols-10 gap-2">
            {[0, 1, 2, 3, 4, 6].map((r) => (
              <button
                key={r}
                onClick={() => ball({ runs: r, extras_type: "none" })}
                disabled={busy || !currentInning?.bowler_id || !currentInning?.started}
                data-testid={`runs-${r}-btn`}
                className={`tactical-btn h-14 sm:h-16 text-2xl border ${
                  r === 4
                    ? "bg-primary text-primary-foreground border-primary hover:bg-[#C9E620]"
                    : r === 6
                    ? "bg-primary text-primary-foreground border-primary hover:bg-[#C9E620] ring-1 ring-primary"
                    : "border-border hover:border-primary text-foreground"
                } disabled:opacity-40 disabled:cursor-not-allowed`}
              >
                {r}
              </button>
            ))}
            <button
              onClick={() => { setExtraType("wide"); setExtraRuns(0); setExtraOpen(true); }}
              disabled={busy || !currentInning?.bowler_id || !currentInning?.started}
              data-testid="wide-btn"
              className="tactical-btn h-14 sm:h-16 text-base border border-border hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              WD
            </button>
            <button
              onClick={() => { setExtraType("no_ball"); setExtraRuns(0); setExtraOpen(true); }}
              disabled={busy || !currentInning?.bowler_id || !currentInning?.started}
              data-testid="no-ball-btn"
              className="tactical-btn h-14 sm:h-16 text-base border border-border hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              NB
            </button>
            <button
              onClick={() => { setExtraType("bye"); setExtraRuns(1); setExtraOpen(true); }}
              disabled={busy || !currentInning?.bowler_id || !currentInning?.started}
              data-testid="bye-btn"
              className="tactical-btn h-14 sm:h-16 text-base border border-border hover:border-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              BYE
            </button>
            <button
              onClick={() => {
                setOutPlayerId(currentInning?.striker_id || "");
                setNewBatsmanId(availableBatsmen[0]?.id || "");
                setWicketType("bowled");
                setWicketRuns(0);
                setWicketExtras("none");
                setWicketOpen(true);
              }}
              disabled={busy || !currentInning?.bowler_id || !currentInning?.started}
              data-testid="wicket-btn"
              className="tactical-btn h-14 sm:h-16 text-xl border bg-destructive text-destructive-foreground border-destructive hover:bg-[#D62B22] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              W
            </button>
          </div>
        </div>
      </div>

      {/* INNINGS SETUP DIALOG */}
      <Dialog open={setupOpen} onOpenChange={(o) => !o && currentInning?.started && setSetupOpen(false)}>
        <DialogContent className="bg-card border-border" data-testid="innings-setup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">
              Innings {match.current_innings + 1} Setup
            </DialogTitle>
            <p className="text-sm text-muted-foreground">{battingTeam?.name} batting · {bowlingTeam?.name} bowling</p>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <SelectField label="Striker" value={strikerId} onChange={setStrikerId} options={battingTeam?.players || []} testId="striker-select" />
            <SelectField label="Non-Striker" value={nonStrikerId} onChange={setNonStrikerId} options={battingTeam?.players.filter((p) => p.id !== strikerId) || []} testId="non-striker-select" />
            <SelectField label="Bowler" value={bowlerSelectId} onChange={setBowlerSelectId} options={bowlingTeam?.players || []} testId="bowler-select" />
          </div>
          <DialogFooter>
            <button
              onClick={submitSetup}
              disabled={busy}
              data-testid="start-innings-btn"
              className="tactical-btn bg-primary text-primary-foreground px-6 py-3 text-sm inline-flex items-center gap-2"
            >
              Start Innings <ChevronRight className="w-4 h-4" strokeWidth={3} />
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW BOWLER DIALOG */}
      <Dialog open={bowlerDialogOpen} onOpenChange={setBowlerDialogOpen}>
        <DialogContent className="bg-card border-border" data-testid="new-bowler-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">New Bowler</DialogTitle>
            <p className="text-sm text-muted-foreground">Over complete. Pick the next bowler.</p>
          </DialogHeader>
          <SelectField label="Bowler" value={newBowlerId} onChange={setNewBowlerId} options={bowlingTeam?.players.filter((p) => p.id !== currentInning?.bowler_id) || []} testId="new-bowler-select" />
          <DialogFooter>
            <button
              onClick={submitNewBowler}
              disabled={busy}
              data-testid="confirm-bowler-btn"
              className="tactical-btn bg-primary text-primary-foreground px-6 py-3 text-sm"
            >
              Confirm
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WICKET DIALOG */}
      <Dialog open={wicketOpen} onOpenChange={setWicketOpen}>
        <DialogContent className="bg-card border-border" data-testid="wicket-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight text-destructive">Wicket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">How Out</label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {["bowled", "caught", "lbw", "run_out", "stumped", "hit_wicket"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setWicketType(t)}
                    data-testid={`wicket-type-${t}`}
                    className={`tactical-btn py-2 text-xs border ${wicketType === t ? "bg-destructive text-destructive-foreground border-destructive" : "border-border hover:border-destructive"}`}
                  >
                    {t.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
            <SelectField label="Out Player" value={outPlayerId} onChange={setOutPlayerId} options={[striker, nonStriker].filter(Boolean)} testId="out-player-select" />
            <SelectField label="New Batsman" value={newBatsmanId} onChange={setNewBatsmanId} options={availableBatsmen} testId="new-batsman-select" />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Runs (run-out etc.)</label>
                <input
                  type="number"
                  min={0}
                  value={wicketRuns}
                  onChange={(e) => setWicketRuns(e.target.value)}
                  data-testid="wicket-runs-input"
                  className="w-full bg-transparent border border-border focus:border-primary focus:outline-none px-3 py-2 mt-2 font-mono"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Extras</label>
                <select
                  value={wicketExtras}
                  onChange={(e) => setWicketExtras(e.target.value)}
                  data-testid="wicket-extras-select"
                  className="w-full bg-card border border-border focus:border-primary focus:outline-none px-3 py-2 mt-2 font-mono text-sm"
                >
                  <option value="none">None</option>
                  <option value="wide">Wide</option>
                  <option value="no_ball">No-ball</option>
                  <option value="bye">Bye</option>
                  <option value="leg_bye">Leg-bye</option>
                </select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setWicketOpen(false)} className="tactical-btn border border-border px-5 py-2 text-xs">Cancel</button>
            <button
              onClick={submitWicket}
              disabled={busy}
              data-testid="confirm-wicket-btn"
              className="tactical-btn bg-destructive text-destructive-foreground px-6 py-3 text-sm"
            >
              Confirm Wicket
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EXTRA DIALOG */}
      <Dialog open={extraOpen} onOpenChange={setExtraOpen}>
        <DialogContent className="bg-card border-border" data-testid="extra-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">
              {extraType === "wide" ? "Wide" : extraType === "no_ball" ? "No-Ball" : extraType === "bye" ? "Bye" : "Leg-Bye"}
            </DialogTitle>
            <p className="text-sm text-muted-foreground">
              {extraType === "wide" ? "Additional runs scored on this wide?" :
                extraType === "no_ball" ? "Bat runs scored off the no-ball?" : "Runs scored as byes?"}
            </p>
          </DialogHeader>
          <div className="grid grid-cols-7 gap-2">
            {[0, 1, 2, 3, 4, 5, 6].map((r) => (
              <button
                key={r}
                onClick={() => setExtraRuns(r)}
                data-testid={`extra-runs-${r}`}
                className={`tactical-btn py-3 text-lg border ${extraRuns === r ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}
              >
                {r}
              </button>
            ))}
          </div>
          {extraType === "no_ball" && (
            <div className="mt-2">
              <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Or Byes/LB on no-ball</label>
              <select
                value={extraType}
                onChange={(e) => setExtraType(e.target.value)}
                className="w-full bg-card border border-border focus:border-primary focus:outline-none px-3 py-2 mt-2 font-mono text-sm"
              >
                <option value="no_ball">No-ball (bat runs)</option>
              </select>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setExtraOpen(false)} className="tactical-btn border border-border px-5 py-2 text-xs">Cancel</button>
            <button
              onClick={submitExtra}
              disabled={busy}
              data-testid="confirm-extra-btn"
              className="tactical-btn bg-primary text-primary-foreground px-6 py-3 text-sm"
            >
              Confirm
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT BALL DIALOG */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-card border-border" data-testid="edit-ball-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">Edit Ball</DialogTitle>
            <p className="text-sm text-muted-foreground">Strike rotation history is preserved.</p>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Type</label>
              <select
                value={editExtras}
                onChange={(e) => setEditExtras(e.target.value)}
                data-testid="edit-extras-select"
                className="w-full bg-card border border-border focus:border-primary focus:outline-none px-3 py-2 mt-2 font-mono text-sm"
              >
                <option value="none">Legal ball</option>
                <option value="wide">Wide</option>
                <option value="no_ball">No-ball</option>
                <option value="bye">Bye</option>
                <option value="leg_bye">Leg-bye</option>
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">Runs</label>
              <div className="grid grid-cols-7 gap-2 mt-2">
                {[0, 1, 2, 3, 4, 5, 6].map((r) => (
                  <button
                    key={r}
                    onClick={() => setEditRuns(r)}
                    data-testid={`edit-runs-${r}`}
                    className={`tactical-btn py-3 text-base border ${
                      editRuns === r ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"
                    }`}
                  >
                    {r}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editIsWicket}
                  onChange={(e) => setEditIsWicket(e.target.checked)}
                  data-testid="edit-is-wicket-toggle"
                  className="w-4 h-4 accent-destructive"
                />
                <span className="text-xs uppercase tracking-[0.2em] font-bold">Wicket</span>
              </label>
              {editIsWicket && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {["bowled", "caught", "lbw", "run_out", "stumped", "hit_wicket"].map((t) => (
                    <button
                      key={t}
                      onClick={() => setEditWicketType(t)}
                      className={`tactical-btn py-2 text-xs border ${
                        editWicketType === t ? "bg-destructive text-destructive-foreground border-destructive" : "border-border hover:border-destructive"
                      }`}
                    >
                      {t.replace("_", " ")}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setEditOpen(false)} className="tactical-btn border border-border px-5 py-2 text-xs">Cancel</button>
            <button
              onClick={submitEditBall}
              disabled={busy}
              data-testid="confirm-edit-ball-btn"
              className="tactical-btn bg-primary text-primary-foreground px-6 py-3 text-sm"
            >
              Save Changes
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* END INNINGS DIALOG */}
      <Dialog open={endInningsOpen} onOpenChange={setEndInningsOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display uppercase text-2xl tracking-tight">End Innings?</DialogTitle>
            <p className="text-sm text-muted-foreground">This will close the current innings and start the next (or end the match).</p>
          </DialogHeader>
          <DialogFooter>
            <button onClick={() => setEndInningsOpen(false)} className="tactical-btn border border-border px-5 py-2 text-xs">Cancel</button>
            <button
              onClick={onEndInnings}
              disabled={busy}
              data-testid="confirm-end-innings-btn"
              className="tactical-btn bg-destructive text-destructive-foreground px-6 py-3 text-sm"
            >
              End Innings
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BatsmanRow({ stats, name, striker }) {
  if (!name) return <div className="text-sm text-muted-foreground italic">—</div>;
  return (
    <div className="flex items-center justify-between border-b border-border pb-2 last:border-0">
      <div className="flex items-center gap-2">
        <span className={`w-1.5 h-1.5 rounded-full ${striker ? "bg-primary" : "bg-transparent border border-border"}`}></span>
        <span className="font-display uppercase font-bold text-base tracking-tight">{name}</span>
        {striker && <span className="text-[10px] text-primary font-mono">*</span>}
      </div>
      <div className="font-mono text-sm flex items-center gap-3 stat-tabular">
        <span className="text-foreground font-bold">{stats?.runs ?? 0}</span>
        <span className="text-muted-foreground">({stats?.balls ?? 0})</span>
        <span className="text-muted-foreground hidden sm:inline">4s {stats?.fours ?? 0}</span>
        <span className="text-muted-foreground hidden sm:inline">6s {stats?.sixes ?? 0}</span>
        <span className="text-muted-foreground hidden sm:inline">SR {stats?.strike_rate ?? "0.00"}</span>
      </div>
    </div>
  );
}

function BowlerRow({ stats, name }) {
  if (!name) return <div className="text-sm text-muted-foreground italic">No bowler set</div>;
  return (
    <div className="flex items-center justify-between">
      <span className="font-display uppercase font-bold text-base tracking-tight">{name}</span>
      <div className="font-mono text-sm flex items-center gap-3 stat-tabular">
        <span>{stats?.overs ?? "0.0"}</span>
        <span className="text-muted-foreground">·</span>
        <span>{stats?.runs ?? 0}/{stats?.wickets ?? 0}</span>
        <span className="text-muted-foreground hidden sm:inline">ER {stats?.economy ?? "0.00"}</span>
      </div>
    </div>
  );
}

function BallChip({ ball, onClick }) {
  let label = String(ball.runs);
  let color = "border-border text-foreground";
  if (ball.is_wicket) { label = "W"; color = "bg-destructive text-destructive-foreground border-destructive"; }
  else if (ball.extras_type === "wide") { label = `WD${ball.extras_runs > 1 ? `+${ball.extras_runs - 1}` : ""}`; color = "border-primary text-primary"; }
  else if (ball.extras_type === "no_ball") { label = `NB${ball.runs ? `+${ball.runs}` : ""}`; color = "border-primary text-primary"; }
  else if (ball.extras_type === "bye") { label = `B${ball.extras_runs}`; color = "border-border text-muted-foreground"; }
  else if (ball.extras_type === "leg_bye") { label = `LB${ball.extras_runs}`; color = "border-border text-muted-foreground"; }
  else if (ball.runs === 4 || ball.runs === 6) { color = "bg-primary text-primary-foreground border-primary"; }
  return (
    <button
      onClick={onClick}
      data-testid={`ball-chip-${ball.over_num}-${ball.ball_num}`}
      className={`min-w-[40px] h-10 px-2 flex items-center justify-center font-display font-black text-sm border ${color} hover:ring-2 hover:ring-primary/60 transition-all`}
      title="Click to edit"
    >
      {label}
    </button>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between border-b border-border pb-1 last:border-0">
      <dt className="text-muted-foreground uppercase text-[10px] tracking-[0.2em]">{k}</dt>
      <dd className="text-foreground">{v}</dd>
    </div>
  );
}

function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.3em] text-muted-foreground font-bold">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-card border border-border focus:border-primary focus:outline-none px-3 py-2 mt-2 font-mono text-sm"
      >
        <option value="">— Select —</option>
        {options.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
    </div>
  );
}
